import { useEffect, useSyncExternalStore } from 'react';
import api, { toApiErrorPayload } from '@/lib/api';
import { QUERY_KEYS } from '@/lib/queryKeys';
import {
  authStore,
  isAuthFresh,
  type UserProfile,
  type AuthStatus,
} from '@/lib/authStore';
import {
  emitAuthSessionEvent,
  emitFromApiError,
  emitLoginSuccess,
  subscribeSessionEvents,
} from '@/lib/auth/sessionCoordinator';

export type { UserProfile };

/**
 * Query key for auth/me (for cache invalidation / setQueryData compatibility)
 * @deprecated Use QUERY_KEYS.auth.me instead
 */
export const AUTH_ME_QUERY_KEY = QUERY_KEYS.auth.me;

const fetchAuthMe = async (): Promise<UserProfile> => {
  const response = await api.get<{ success: boolean; message?: string; data?: UserProfile }>('/auth/me');
  if (!response.ok || !response.data?.success) {
    throw toApiErrorPayload(response.data);
  }
  return response.data.data as UserProfile;
};

let inFlightPromise: Promise<void> | null = null;

/**
 * Bootstrap /api/auth/me once per session (single-flight + 60s fresh).
 * Call when token exists; idempotent.
 */
export function bootstrapAuth(): Promise<void> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) {
    authStore.setUnauthenticated();
    return Promise.resolve();
  }

  const current = authStore.getState();
  if (current.status === 'loading' && inFlightPromise) {
    return inFlightPromise;
  }
  if (current.status === 'authenticated' && current.user && isAuthFresh()) {
    return Promise.resolve();
  }

  inFlightPromise = (async () => {
    authStore.setState({ status: 'loading', error: null }, 'bootstrapAuth_start');
    try {
      const user = await fetchAuthMe();
      emitLoginSuccess('bootstrapAuth:/auth/me');
      emitAuthSessionEvent({
        type: 'RECOVERED',
        source: 'bootstrapAuth:/auth/me',
        at: Date.now(),
      });
      authStore.setState(
        {
          status: 'authenticated',
          user,
          lastCheckedAt: Date.now(),
          error: null,
        },
        'bootstrapAuth_success'
      );
    } catch (err) {
      const detail = toApiErrorPayload(err);
      emitFromApiError(detail, 'bootstrapAuth:/auth/me');

      if (detail.kind === 'network') {
        authStore.setOffline(new Error(detail.message));
      } else if (detail.kind === 'server') {
        authStore.setDegraded(new Error(detail.message));
      } else if (detail.kind !== 'auth') {
        authStore.setDegraded(new Error(detail.message));
      }
    } finally {
      inFlightPromise = null;
    }
  })();
  return inFlightPromise;
}

function selectAuthForHook(state: { status: AuthStatus; user: UserProfile | null; error: Error | null }) {
  return {
    data: state.user,
    isLoading: state.status === 'unknown' || state.status === 'loading',
    isError: state.status === 'offline' || state.status === 'degraded',
    error: state.error,
    status: state.status,
  };
}

// Stable snapshot for useSyncExternalStore: MUST return same reference when store unchanged (React #185).
let cachedState: { status: AuthStatus; user: UserProfile | null; error: Error | null } | null = null;
let cachedSnapshot: ReturnType<typeof selectAuthForHook> | null = null;

function getClientSnapshot(): ReturnType<typeof selectAuthForHook> {
  const current = authStore.getState();
  if (cachedState === current && cachedSnapshot !== null) return cachedSnapshot;
  cachedState = current;
  cachedSnapshot = selectAuthForHook(current);
  return cachedSnapshot;
}

const SERVER_SNAPSHOT = selectAuthForHook({
  status: 'unknown',
  user: null,
  error: null,
});

function getServerSnapshot(): ReturnType<typeof selectAuthForHook> {
  return SERVER_SNAPSHOT;
}

/**
 * Hook: auth/me from authStore. Triggers bootstrapAuth once when token exists.
 * /api/auth/me is called at most once per bootstrap (single-flight + 60s fresh).
 * No refetchOnWindowFocus / refetchOnMount => no loading flip/flop.
 */
export function useAuthMe() {
  const state = useSyncExternalStore(
    authStore.subscribe,
    getClientSnapshot,
    getServerSnapshot
  );

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  useEffect(() => {
    if (token) bootstrapAuth();
  }, [token]);

  useEffect(() => {
    const unsub = subscribeSessionEvents((event) => {
      if (event.type === 'LOGIN_SUCCESS') {
        bootstrapAuth().catch(() => {
          // no-op
        });
      }
    });
    return unsub;
  }, []);

  return state;
}
